import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FeedbackOption } from '../Self-Assessment/self-assessment.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
const DEFAULT_DOMAIN = '';
const STATIC_FEEDBACK_JWT =
  '';
  const DEFAULT_FEEDBACK_TYPE = 'EMPLOYEE_FEEDBACK';
const STATIC_ENCRYPTED_PAYROLL_ID_ENCODED = '';
export interface UserProfileResponse {
  basicInfo?: {
    designation?: string;
    subject?: string;
    department?: string;
  };
  employeeDashBoardInfo?: {
    empName?: string;
    campusName?: string;
    payrollId?: string;
    tempPayrollId?: string;
  };
}

export interface ScenarioAssessmentOptionDto {
  optionId: number;
  optionText: string;
  optionSequence: string; // "A"|"B"|"C"|"D"
}

export interface ScenarioAssessmentQuestionDto {
  questionId: number;
  questionTitle: string;
  questionText: string;
  questionScenario: string;
  optionsList: ScenarioAssessmentOptionDto[];
}

export interface ScenarioAssessmentSectionDto {
  examId: number;
  sectionId: number;
  sectionName: string;
  questionDetailsList: ScenarioAssessmentQuestionDto[];
}

export interface ScenarioAssessmentAttemptedQuestionPayload {
  questionId: number;
  optionId: number;
  timeTaken: number;
}

export interface ScenarioAssessmentAttemptSectionPayload {
  sectionId: number;
  attemptedQuestions: ScenarioAssessmentAttemptedQuestionPayload[];
}

export interface ScenarioAssessmentSavePayload {
  examId: number;
  attemptStartTime: number;
  attemptendTIme: number;
  sections: ScenarioAssessmentAttemptSectionPayload[];
  attemptedCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class ScenarioAssessmentService {
  private readonly API_ORIGIN = String((environment as any).masterDomain || DEFAULT_DOMAIN).trim().replace(/\/+$/, '');
  private readonly userProfileUrl = `${this.API_ORIGIN}/employee/api/jwtV2/common/userProfile`;
  private readonly scenarioQuestionsUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empFeedBack/pplSelfAssesment/questionAndOptionDetails`;
  private readonly scenarioSaveUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empFeedBack/pplSelfAssesment/saveExamAttemptDetails`;

  private readonly cachedOptionsByQuestionId = new Map<number, Observable<FeedbackOption[]>>();

  private readonly tokenStorageKeys = ['updated', 'jwtToken', 'token', 'authToken', 'accessToken'] as const;
  private readonly payrollStorageKeys = [ 'userId'] as const;
  private readonly encryptedPayrollStorageKeys = [ 'uniqueRefId',] as const;
  headers: any;

  private get authHeaderValue(): string {
    let raw: string | null = null;
    for (const key of this.tokenStorageKeys) {
      raw = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (raw && raw.trim()) break;
    }
    const trimmed = (raw || '').trim();
    if (trimmed) {
      if (/^Bearer\s+/i.test(trimmed)) return trimmed;
      return `Bearer ${trimmed}`;
    }
    return `Bearer ${STATIC_FEEDBACK_JWT}`;
  }

 constructor(private readonly http: HttpClient) {}
  getStoredPayrollId(): string {
    for (const key of this.payrollStorageKeys) {
      const v = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (v && String(v).trim()) return String(v).trim();
    }
    // Keep behavior consistent with other modules in this workspace.
    // Swagger example uses hyd110005779 (with two zeros).
    return '';
  }

  /** For userProfile API which expects an encrypted payroll id (base64-like). */
  getStoredEncryptedPayrollId(): string | null {
    for (const key of this.encryptedPayrollStorageKeys) {
      const v = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (v && String(v).trim()) return String(v).trim();
    }
    // Static fallback when storage doesn't have encrypted value.
    return STATIC_ENCRYPTED_PAYROLL_ID_ENCODED;
  }
  getUserProfile(payrollId: string): Observable<UserProfileResponse> {
    const raw = String(payrollId ?? '').trim();
    // Accept already-encoded encrypted payroll ids (e.g. FS%2F...%3D) without double-encoding.
    const pid = raw.includes('%') ? raw : encodeURIComponent(raw);
    const url = `${this.userProfileUrl}?payrollId=${pid}`;
    return this.http.get<UserProfileResponse>(url, { headers: this.headers });
  }
    /** Scenario-based assessment: section-wise questions with options (A/B/C/D). */
  getScenarioAssessmentQuestionAndOptionDetails(): Observable<ScenarioAssessmentSectionDto[]> {
    return this.http.get<ScenarioAssessmentSectionDto[]>(this.scenarioQuestionsUrl, { headers: this.headers }).pipe(
      map((res) => (Array.isArray(res) ? res : []))
    );
  }
    private get imageAuthHeaders(): HttpHeaders {
    let h = new HttpHeaders();
    const auth = this.authHeaderValue;
    if (auth) h = h.set('Authorization', auth);
    return h;
  }
  getProfileImageBlob(options?: {
    pathSegment?: string;
    /** Prefer `employeeDashBoardInfo.payrollId` (fallback: tempPayrollId, then storage). */
    payrollId?: string;
    /** Same profile response — send as `tempPayrollId` query param when present. */
    tempPayrollId?: string;
    query?: Record<string, string>;
    /**
     * When true (default), adds `width` / `height` query hints (DPR-aware, up to 1024px) so the server can return a larger bitmap.
     * Set false if your API rejects unknown params.
     */
    preferHighResolution?: boolean;
  }): Observable<Blob> {
    const rawSeg = (options?.pathSegment ?? 'userId').trim() || 'userId';
    const segment = encodeURIComponent(rawSeg);
    const qs = new URLSearchParams();
    const temp = String(options?.tempPayrollId ?? '').trim();
    const primary =
      String(options?.payrollId ?? '').trim() || temp || this.getStoredPayrollId();
    if (primary) qs.set('payrollId', primary);
    if (temp) qs.set('tempPayrollId', temp);
    if (options?.query) {
      for (const [k, v] of Object.entries(options.query)) {
        if (v != null && String(v).trim()) qs.set(k, String(v));
      }
    }
    const preferHi = options?.preferHighResolution !== false;
    if (preferHi) {
      const maxPx = 1024;
      const basePerDpr = 320;
      let px = 512;
      try {
        const dpr = Math.min(3, Math.max(1, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1));
        px = Math.min(maxPx, Math.round(basePerDpr * dpr));
      } catch {
        px = 512;
      }
      if (!qs.has('width')) qs.set('width', String(px));
      if (!qs.has('height')) qs.set('height', String(px));
    }
    const queryString = qs.toString();
    const base = `${this.API_ORIGIN}/employee/api/jwtV2/${segment}/profileImage`;
    const url = queryString ? `${base}?${queryString}` : base;
    const imgHeaders = this.imageAuthHeaders.set(
      'Accept',
      'image/avif,image/webp,image/apng,image/jpeg,image/png,image/*,*/*;q=0.8'
    );
    return this.http.get(url, { headers: imgHeaders, responseType: 'blob' }).pipe(
      catchError(() => of(new Blob()))
    );
  }
  saveScenarioAssessmentAttempt(payload: ScenarioAssessmentSavePayload): Observable<unknown> {
    return this.http.post(this.scenarioSaveUrl, payload, { headers: this.headers });
  }

}
